import {
  inspectionStatusClasses,
  inspectionStatusIcon,
  inspectionStatusLabel,
  inspectionStatusRingClasses,
  type InspectionStatus,
} from "../../store/oasis/yardInspectionsStore";

/**
 * El orden en el que se pintan los estados, de "salió bien" a "no aplica".
 *
 * Vive acá y no en cada pantalla porque ahora son dos las que lo pintan: la
 * tira del historial de inspecciones y la tarjeta de patio (#499). El comentario
 * de `INSPECTION_STATUS_PALETTE` ya advierte qué pasa cuando el vocabulario se
 * copia en vez de compartirse — tres copias del mapa de colores es cómo
 * `missing_exit` llegó a producción pintado en la píldora y neutro en la
 * tarjeta de atrás.
 */
export const INSPECTION_STATUS_ORDER: InspectionStatus[] = [
  "complete",
  "pending_exit",
  "missing_exit",
  "missing_vehicle",
  "incomplete",
  // Los dos del tag de proveedores van juntos y pegados a "Cancelada", que es
  // el proveedor de antes: las tres son visitas sin descarga.
  "provider_pending",
  "provider_complete",
  "cancelled",
];

/**
 * Píldora de estado con su conteo.
 *
 * Nace como el `StatusPill` del historial de inspecciones
 * (inspectionHistorySection.tsx) y se comparte tal cual: mismo hue, mismo
 * ícono, misma etiqueta. La única diferencia es que el conteo de la tarjeta de
 * patio no filtra nada, así que `onClick` es opcional y sin él se renderiza un
 * `<span>` — un `<button>` que no hace nada invita a hacerle clic y además lo
 * anuncia como accionable a un lector de pantalla.
 */
export function InspectionStatusPill({
  status,
  count,
  active = false,
  onClick,
  title,
  compact = false,
}: {
  status: InspectionStatus;
  count: number;
  active?: boolean;
  onClick?: () => void;
  /** Aclaración al pasar el mouse; hoy la usa `complete` (ver YardOperationSummary). */
  title?: string;
  /** Tamaño de tarjeta: mismo hue, menos aire. */
  compact?: boolean;
}) {
  const size = compact
    ? "text-[11px] px-2 py-0.5 gap-1"
    : "text-xs px-3 py-1.5 gap-1.5";
  const className = `font-medium rounded-full inline-flex items-center ${size} ${inspectionStatusClasses(status)}`;

  const content = (
    <>
      <i className={`${inspectionStatusIcon(status)} ${compact ? "text-xs" : "text-sm"}`}></i>
      <span>{inspectionStatusLabel(status)}</span>
      <span className="font-bold">{count}</span>
    </>
  );

  if (!onClick) {
    return (
      <span className={className} title={title}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`${className} cursor-pointer transition-all hover:brightness-110 ${
        active
          ? `ring-2 ring-offset-2 ring-offset-transparent ${inspectionStatusRingClasses(status)}`
          : ""
      }`}
    >
      {content}
    </button>
  );
}

export default InspectionStatusPill;
